'use client'

import * as React from 'react'

import { nextVisibleCount, REVEAL_CHUNK, REVEAL_ROOT_MARGIN, shouldRevealMore } from '../reveal'

/**
 * Windowed "reveal on scroll" for bounded option lists (dropdown popovers,
 * pickers). Render `visibleCount` rows, put `sentinelRef` on an element after
 * them, and the hook grows the window through four cooperating paths:
 *
 *  1. IntersectionObserver on the sentinel (prefetches before the bottom).
 *  2. A throttled scroll-listener backstop (`shouldRevealMore`) — observers
 *     can miss their first fire inside animating portals; scrolling never does.
 *  3. An rAF-deferred initial check, for regions that don't overflow yet
 *     (small `initial`, popover open animation still running).
 *  4. An idle-time top-up loop (`requestIdleCallback`, `setTimeout` fallback):
 *     while the list is open and more rows exist, one chunk per idle callback
 *     is revealed in the background so the full list is usually ready before
 *     the user ever reaches the bottom — chunked so long lists never jank.
 *
 * Every path re-arms on each `visibleCount` change — a missed event can delay
 * a reveal, never freeze the list. Note for future keyboard nav: highlight
 * movement past the window edge should call `revealMore()` directly.
 */
export interface UseRevealOnScrollOptions {
  /** Total rows available in the (already filtered) list. */
  total: number
  /** When this changes (new query, re-open), the window resets to `initial`. */
  resetKey?: unknown
  /** First-chunk size. Defaults to REVEAL_CHUNK. */
  initial?: number
  /** Rows added per reveal. Defaults to REVEAL_CHUNK. */
  chunk?: number
  /** Gate for all wiring (pass the popover's `open`). Defaults to true. */
  enabled?: boolean
}

export interface UseRevealOnScrollResult {
  scrollRegionRef: React.RefObject<HTMLDivElement | null>
  sentinelRef: React.RefObject<HTMLDivElement | null>
  visibleCount: number
  hasMore: boolean
  /** Imperative single-chunk reveal (fallback buttons, keyboard nav). */
  revealMore: () => void
}

const SCROLL_THROTTLE_MS = 100
const IDLE_FALLBACK_MS = 200

type IdleHandle = { kind: 'idle'; id: number } | { kind: 'timeout'; id: ReturnType<typeof setTimeout> }

function scheduleIdle(cb: () => void): IdleHandle {
  if (typeof requestIdleCallback === 'function') {
    return { kind: 'idle', id: requestIdleCallback(() => cb(), { timeout: 1000 }) }
  }
  return { kind: 'timeout', id: setTimeout(cb, IDLE_FALLBACK_MS) }
}

function cancelIdle(handle: IdleHandle) {
  if (handle.kind === 'idle') {
    cancelIdleCallback(handle.id)
  } else {
    clearTimeout(handle.id)
  }
}

export function useRevealOnScroll({
  total,
  resetKey,
  initial = REVEAL_CHUNK,
  chunk = REVEAL_CHUNK,
  enabled = true,
}: UseRevealOnScrollOptions): UseRevealOnScrollResult {
  const [visibleCount, setVisibleCount] = React.useState(initial)
  const scrollRegionRef = React.useRef<HTMLDivElement>(null)
  const sentinelRef = React.useRef<HTMLDivElement>(null)

  // A new resetKey (query change, fresh open) is a new list — back to the top.
  React.useEffect(() => {
    setVisibleCount(initial)
  }, [resetKey, initial])

  const hasMore = visibleCount < total

  const revealMore = React.useCallback(() => {
    setVisibleCount((c) => nextVisibleCount(c, total, chunk))
  }, [total, chunk])

  React.useEffect(() => {
    if (!enabled || !hasMore) {
      return
    }
    const reveal = () => setVisibleCount((c) => nextVisibleCount(c, total, chunk))
    const root = scrollRegionRef.current
    const sentinel = sentinelRef.current
    const cleanups: Array<() => void> = []

    if (root && sentinel && typeof IntersectionObserver !== 'undefined') {
      const observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((e) => e.isIntersecting)) {
            reveal()
          }
        },
        { root, rootMargin: REVEAL_ROOT_MARGIN },
      )
      observer.observe(sentinel)
      cleanups.push(() => observer.disconnect())
    }

    if (root) {
      let throttled = false
      const onScroll = () => {
        if (throttled) {
          return
        }
        throttled = true
        setTimeout(() => {
          throttled = false
          if (shouldRevealMore(root, true)) {
            reveal()
          }
        }, SCROLL_THROTTLE_MS)
      }
      root.addEventListener('scroll', onScroll, { passive: true })
      cleanups.push(() => root.removeEventListener('scroll', onScroll))

      // Deferred initial check: if the region doesn't overflow yet (short
      // first chunk, open animation still measuring), top it up now.
      if (typeof requestAnimationFrame === 'function') {
        const raf = requestAnimationFrame(() => {
          if (shouldRevealMore(root, true)) {
            reveal()
          }
        })
        cleanups.push(() => cancelAnimationFrame(raf))
      }
    }

    // Idle top-up: opportunistically finish loading the whole list while the
    // browser has nothing better to do. One chunk per callback; the effect
    // re-runs on visibleCount change, so this self-chains until exhausted.
    const idle = scheduleIdle(reveal)
    cleanups.push(() => cancelIdle(idle))

    return () => {
      for (const c of cleanups) {
        c()
      }
    }
  }, [enabled, hasMore, total, chunk, visibleCount])

  return { scrollRegionRef, sentinelRef, visibleCount, hasMore, revealMore }
}
